import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Table,
  Space,
  Tooltip,
  Modal,
  Form,
  Typography,
  InputNumber,
  Select,
  Divider,
  Tag,
  DatePicker
} from 'antd';
import { EditFilled, DeleteFilled, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import {
  apiGetAllMeterInformation,
  apiGetMeterInformationById,
  apiCreateMeterInformation,
  apiUpdateMeterInformation,
  apiDeleteMeterInformation
} from '@services/ManageMeters/MeterServices';
import { apiGetMasterData } from '@services/MasterData/MasterDataService';
import { showError, showSuccess } from '@components/notification';
import dayjs from 'dayjs';

const { Option } = Select;

const PULSE_RATIO_OPTIONS = [
  { value: '1', label: '1Lít/Xung' },
  { value: '2', label: '10Lít/Xung' },
  { value: '3', label: '100Lít/Xung' },
  { value: '4', label: '1000Lít/Xung' },
  { value: '5', label: '0.5Lít/Xung' },
  { value: '6', label: '5Lít/Xung' },
  { value: '9', label: '3Lít/Xung' },
  { value: '10', label: '30Lít/Xung' }
];

const MAX_READING_OPTIONS = [
  { value: '999999', label: '999,999' },
  { value: '9999999', label: '9,999,999' },
  { value: '99999999', label: '99,999,999' },
  { value: '999999999', label: '999,999,999' },
  { value: '9999999999', label: '9,999,999,999' },
  { value: '99999999999', label: '99,999,999,999' },
  { value: '999999999999', label: '999,999,999,999' }
];

const EMPTY_FORM = {
  name: undefined,
  serial_number: '',
  size: undefined,
  type: '',
  style: '',
  inspection_date: null,
  year_valid: null,
  max_reading: undefined,
  pulse_ratio: undefined
};

export default function List() {
  const [dataList, setDataList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [flagSearch, setFlagSearch] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // Master data for meter name & size
  const [meterNameOptions, setMeterNameOptions] = useState([]);
  const [meterSizeOptions, setMeterSizeOptions] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);

  const fetchMeterMasterData = async () => {
    try {
      setLoadingMasterData(true);
      const [nameRes, sizeRes] = await Promise.all([
        apiGetMasterData({ type: 'METER_NAME', page: 1, size: 200 }),
        apiGetMasterData({ type: 'METER_SIZE', page: 1, size: 200 })
      ]);
      setMeterNameOptions(
        (nameRes?.data?.data ?? []).map((item) => ({ label: item.name, value: item.value }))
      );
      setMeterSizeOptions(
        (sizeRes?.data?.data ?? []).map((item) => ({ label: item.name, value: item.value }))
      );
    } catch (e) {
      console.error('[fetchMeterMasterData]', e);
    } finally {
      setLoadingMasterData(false);
    }
  };

  useEffect(() => {
    fetchMeterMasterData();
  }, []);

  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllMeterInformation({
        page: currentPage,
        size: pageSize,
        keyWord: search?.trim() || undefined
      });
      setDataList(
        (res.data?.data ?? []).map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total ?? 0);
    } catch (err) {
      showError('Không thể tải dữ liệu!');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch]);

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch((f) => !f);
  };

  const openCreateModal = () => {
    setEditingId(null);
    form.setFieldsValue(EMPTY_FORM);
    setModalVisible(true);
  };

  const openEditModal = async (record) => {
    setEditingId(record.id);
    try {
      const res = await apiGetMeterInformationById(record.id);
      const data = res.data?.data ?? record;
      form.setFieldsValue({
        name: data.name || undefined,
        serial_number: data.serial_number ?? '',
        size: data.size || undefined,
        type: data.type ?? '',
        style: data.style ?? '',
        inspection_date: data.inspection_date ? dayjs(data.inspection_date) : null,
        year_valid: data.year_valid ?? null,
        max_reading: data.max_reading || undefined,
        pulse_ratio: data.pulse_ratio || undefined
      });
    } catch {
      form.setFieldsValue({ ...record });
    }
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (values.inspection_date) {
        values.inspection_date = dayjs(values.inspection_date).format('YYYY-MM-DD');
      }
      setModalLoading(true);
      if (editingId) {
        await apiUpdateMeterInformation(editingId, values);
        showSuccess({ message: 'Cập nhật đồng hồ thành công!' });
      } else {
        await apiCreateMeterInformation(values);
        showSuccess({ message: 'Thêm mới đồng hồ thành công!' });
      }
      setModalVisible(false);
      setCurrentPage(1);
      setFlagSearch((f) => !f);
    } catch (err) {
      if (err?.errorFields) return; // validation error, do nothing
      showError('Có lỗi xảy ra, vui lòng thử lại!');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Bạn có chắc chắn muốn xoá đồng hồ "${record.name || record.serial_number}"?`,
      okText: 'Xoá',
      okType: 'danger',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await apiDeleteMeterInformation(record.id);
          showSuccess({ message: 'Xoá đồng hồ thành công!' });
          fetchData();
        } catch {
          showError('Xoá thất bại!');
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 60,
        fixed: 'left'
      },
      // {
      //   title: 'Trạng thái',
      //   dataIndex: 'status',
      //   width: 130,
      //   render: (val) =>
      //     val === 'installed' ? (
      //       <Tag color="green">Đang lắp đặt</Tag>
      //     ) : (
      //       <Tag color="default">Lưu kho</Tag>
      //     )
      // },
      {
        title: 'Tên đồng hồ',
        dataIndex: 'name',
        width: 150
      },
      {
        title: 'Số serial',
        dataIndex: 'serial_number',
        width: 150
      },
      {
        title: 'Kích thước',
        dataIndex: 'size',
        width: 100
      },
      {
        title: 'Loại',
        dataIndex: 'type',
        width: 120
      },
      {
        title: 'Kiểu dáng',
        dataIndex: 'style',
        width: 120
      },
      {
        title: 'Ngày kiểm định',
        dataIndex: 'inspection_date',
        width: 130,
        render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—')
      },
      {
        title: 'Năm hiệu lực',
        dataIndex: 'year_valid',
        width: 110
      },
      {
        title: 'Chỉ số max',
        dataIndex: 'max_reading',
        width: 110
      },
      {
        title: 'Tỉ số xung',
        dataIndex: 'pulse_ratio',
        width: 110
      },

      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Chỉnh sửa">
              <Button
                type="text"
                shape="circle"
                icon={<EditFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
            <Tooltip title="Xoá">
              <Button
                type="text"
                shape="circle"
                icon={<DeleteFilled style={{ color: '#e11d48', fontSize: '18px' }} />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          </Space>
        )
      }
    ],
    [dataList]
  );

  return (
    <div className="dashboard">
      {/* Header row */}
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4}>Quản lý đồng hồ</Typography.Title>
        </Col>
        <Col>
          <Button
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{
              color: '#0284c7',
              height: '36px',
              backgroundColor: 'var(--surface-3)',
              borderColor: 'var(--border)',
              fontSize: '18px'
            }}>
            Thêm mới
          </Button>
        </Col>
      </Row>

      {/* Filter card */}
      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={10}>
            <Input
              placeholder="Tìm kiếm theo tên, số serial, loại..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: '100%', height: 36 }}
            />
          </Col>
          <Col>
            <Button
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                height: 36,
                backgroundColor: '#0284c7',
                borderColor: '#0284c7',
                color: '#fff'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table card */}
      <Card bodyStyle={{ padding: 0 }}>
        <Table
          bordered
          loading={isFetching}
          columns={columns}
          dataSource={dataList}
          rowKey="id"
          scroll={{ x: 'max-content', y: 600 }}
          style={{ borderRadius: 8 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`,
            onChange: (page) => setCurrentPage(page),
            onShowSizeChange: (_, size) => {
              setPageSize(size);
              setCurrentPage(1);
            }
          }}
        />
      </Card>

      <Modal
        title={editingId ? 'Cập nhật thông tin đồng hồ' : 'Thêm mới đồng hồ'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={modalLoading}
        okText={editingId ? 'Cập nhật' : 'Thêm mới'}
        cancelText="Huỷ"
        width={720}
        destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* <Divider
            style={{ borderColor: '#0284c7', color: '#0284c7', marginBottom: 16 }}
            orientation="left">
            Thông tin đồng hồ
          </Divider> */}
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item label="Số serial" name="serial_number">
                <Input placeholder="Nhập số serial đồng hồ" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Tên đồng hồ" name="name">
                <Select
                  placeholder="Chọn tên đồng hồ"
                  loading={loadingMasterData}
                  showSearch
                  optionFilterProp="label"
                  options={meterNameOptions}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Cỡ đồng hồ" name="size">
                <Select
                  placeholder="Chọn cỡ đồng hồ"
                  loading={loadingMasterData}
                  showSearch
                  optionFilterProp="label"
                  options={meterSizeOptions}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Hệ số xung" name="pulse_ratio">
                <Select placeholder="Chọn hệ số xung" allowClear style={{ width: '100%' }}>
                  {PULSE_RATIO_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Chỉ số cực đại" name="max_reading">
                <Select placeholder="Chọn chỉ số cực đại" allowClear style={{ width: '100%' }}>
                  {MAX_READING_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Loại đồng hồ" name="type">
                <Input placeholder="Nhập loại đồng hồ" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Kiểu dáng" name="style">
                <Input placeholder="Nhập kiểu dáng" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Ngày kiểm định" name="inspection_date">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày kiểm định"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Năm hiệu lực (số năm)" name="year_valid">
                <InputNumber style={{ width: '100%' }} placeholder="Ví dụ: 5" min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
